import React from 'react'
import { useI18n } from '../../../i18n'
import { Button } from '../../ui/Button'
import listStyles from '../../../styles/pages/governance/GovernanceListing.module.scss'

type EvidenceExpectation = { requirementKey: string; title: string }

type Props = {
  controlKey: string
  title?: string
  description?: string | null
  soc2Criteria?: string | null
  criteriaReference?: string | null
  standardCode?: string
  evidenceExpectations?: EvidenceExpectation[]
  refreshing?: boolean
  onRefresh?: () => void
  /** Sequential mode: e.g. "Control 1 / 25" */
  progressEyebrow?: string
  /** Sequential mode: e.g. "24 controls remaining" */
  progressBadge?: string | null
}

export function ControlContextPanel({
  controlKey,
  title,
  description,
  soc2Criteria,
  criteriaReference,
  standardCode,
  evidenceExpectations,
  refreshing,
  onRefresh,
  progressEyebrow,
  progressBadge,
}: Props) {
  const { t } = useI18n()

  return (
    <section className={listStyles.panel}>
      {progressEyebrow || progressBadge ? (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          {progressEyebrow ? (
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{progressEyebrow}</p>
          ) : (
            <span />
          )}
          {progressBadge ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-900">
              {progressBadge}
            </span>
          ) : null}
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{controlKey}</p>
          {title ? <h3 className="text-lg font-semibold text-slate-900">{title}</h3> : null}
          {criteriaReference || soc2Criteria ? (
            <p className="mt-1 text-sm text-slate-600">
              {t('controlCentric.control.standardReference', 'Standard')}: {criteriaReference || soc2Criteria}
              {standardCode && standardCode !== 'SOC2' ? ` · ${standardCode}` : ''}
            </p>
          ) : null}
          {description ? <p className="mt-1 text-sm text-slate-600">{description}</p> : null}
          {refreshing ? (
            <p className="mt-1 text-[10px] text-slate-500">{t('common.refreshing', 'Refreshing…')}</p>
          ) : null}
        </div>
        {onRefresh ? (
          <Button type="button" size="small" variant="outline" disabled={refreshing} onClick={onRefresh}>
            {t('common.refresh', 'Refresh')}
          </Button>
        ) : null}
      </div>
      {evidenceExpectations?.length ? (
        <>
          <p className="mt-3 border-t border-slate-100 pt-3 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('controlCentric.evidence.requiredList', 'Required evidence')}
          </p>
          <ul className="mt-2 flex flex-wrap gap-1">
          {evidenceExpectations.map((e) => (
            <li key={e.requirementKey} className="rounded-md bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">
              {e.title}
            </li>
          ))}
          </ul>
        </>
      ) : null}
    </section>
  )
}
